import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { isAxiosError } from 'axios';
import {
  fetchHistoricosIntegracao,
  historicoIntegracaoKeys,
} from '@/services/historicoIntegracaoService';
import type { IHistoricoIntegracao } from '@/types/historicoIntegracao';
import { Table } from '@/components/Table/Table';
import './historicos-integracao.css';

export const Route = createFileRoute('/_authenticated/relatorio/historicos-integracao/')({
  component: HistoricosIntegracaoPage,
});

function extractApiError(error: unknown, fallback: string): string {
  if (isAxiosError(error)) {
    const data = error.response?.data as { error?: string | { message?: string } } | undefined;
    if (typeof data?.error === 'string') return data.error;
    if (data?.error && typeof data.error === 'object' && data.error.message) {
      return data.error.message;
    }
    return error.message;
  }
  if (error instanceof Error) return error.message;
  return fallback;
}

function formatDt(value: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString('pt-BR');
}

function statusLabel(status: IHistoricoIntegracao['status']): string {
  if (status === 'executando') return 'Executando';
  if (status === 'sucesso') return 'Sucesso';
  return 'Erro';
}

function HistoricosIntegracaoPage() {
  const { data = [], isLoading, isError, error } = useQuery({
    queryKey: historicoIntegracaoKeys.list(),
    queryFn: fetchHistoricosIntegracao,
  });

  return (
    <div className="historicos-page">
      <div className="historicos-page__header">
        <div>
          <h1>Histórico de Integrações</h1>
          <p className="historicos-page__subtitle">
            Execuções de sincronização por integração e recurso.
          </p>
        </div>
      </div>

      {isError && (
        <p className="historicos-page__error" role="alert">
          {extractApiError(error, 'Falha ao carregar histórico.')}
        </p>
      )}

      <div className="historicos-page__table-container">
        <Table>
          <Table.Header>
            <Table.Row>
              <Table.Head scope="col">Início</Table.Head>
              <Table.Head scope="col">Fim</Table.Head>
              <Table.Head scope="col">Recurso</Table.Head>
              <Table.Head scope="col">Status</Table.Head>
              <Table.Head scope="col">Encontrados</Table.Head>
              <Table.Head scope="col">Salvos</Table.Head>
              <Table.Head scope="col">Erro</Table.Head>
            </Table.Row>
          </Table.Header>
          <Table.Content>
            {isLoading && (
              <Table.Row>
                <Table.Cell colSpan={7}>Carregando…</Table.Cell>
              </Table.Row>
            )}
            {!isLoading && data.length === 0 && (
              <Table.Row>
                <Table.Cell colSpan={7}>Nenhum histórico encontrado.</Table.Cell>
              </Table.Row>
            )}
            {data.map((row) => (
              <Table.Row key={row.id}>
                <Table.Cell>{formatDt(row.iniciado_em)}</Table.Cell>
                <Table.Cell>{formatDt(row.finalizado_em)}</Table.Cell>
                <Table.Cell>{row.recurso}</Table.Cell>
                <Table.Cell>
                  <span className={`historicos-page__status is-${row.status}`}>
                    {statusLabel(row.status)}
                  </span>
                </Table.Cell>
                <Table.Cell>{row.registros_encontrados}</Table.Cell>
                <Table.Cell>{row.registros_salvos}</Table.Cell>
                <Table.Cell className="historicos-page__erro">
                  {row.mensagem_erro || '—'}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Content>
        </Table>
      </div>
    </div>
  );
}
